import { useQuery } from "@tanstack/react-query";
import { getCoachEngagement } from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import type { CoachEngagement, CoachEngagementFilters } from "@/lib/types";

/**
 * Coach engagement dashboard data hook (M6a).
 *
 * The dashboard is filter-driven — the query key carries the
 * active `filters` in its `extra` slot so changing a filter
 * advances the key and refetches (each distinct filter combo
 * gets its own cache bucket). Scope-keyed + gated on `teamId`
 * like the other M6a coach queries.
 *
 * Returns the unwrapped `CoachEngagement` payload (the verb peels
 * the route's `{ engagement: ... }` envelope).
 */

export function useCoachEngagement(filters: CoachEngagementFilters) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery<CoachEngagement>({
    // The filters object goes in `extra` — `useScopedKey` keeps
    // the `[domain, teamId, seasonId, extra]` tuple shape so the
    // `invalidateScope` predicate still matches.
    queryKey: useScopedKey("coach-engagement", {
      filters: filters as Record<string, unknown>,
    }),
    queryFn: () => getCoachEngagement(filters),
    enabled: !!teamId,
  });
}
